import { Dialog, Transition } from '@headlessui/react';
import Attention from 'assets/icons/Attention';
import Right from 'assets/icons/Right';
import Button from 'common/components/Button';
import Loader from 'common/components/Loader';
import useNavigate from 'common/hooks/useNavigate';
import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { useDeleteOfferMutation } from 'common/slices/offerApi.slice';
import { cn } from 'common/utils';
import React, { Fragment } from 'react';

const DeleteModal = ({ offerId, isOpen, setIsOpen }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation('offers\\details');

  const [deleteOffer, { isLoading }] = useDeleteOfferMutation();

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleDelete = async () => {
    try {
      await deleteOffer({ offerId }).unwrap();

      toast.newToast('positive', t('modals.delete.toasts.success'));

      navigate('/dashboard');
    } catch (err) {
      switch (err?.status) {
        case 403:
          toast.newToast('negative', t('modals.delete.toasts.forbidden'));
          break;
        default:
          toast.newToast('negative', t('modals.delete.toasts.something-wrong'));
      }
    } finally {
      closeModal();
    }
  };

  return (
    <Fragment>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={isLoading ? () => null : closeModal}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-25' />
          </Transition.Child>
          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel
                  className={cn(
                    'flex flex-col w-full max-w-md transform overflow-hidden',
                    'rounded bg-white p-10 text-left gap-6',
                    'align-middle shadow-xl transition-all relative',
                  )}
                >
                  <Dialog.Title
                    as='div'
                    className='flex flex-col w-full items-center gap-8'
                  >
                    <Attention size='64px' color='#084D6E' />
                    <h1 className=' text-2xl font-semibold text-dark-charcoal'>
                      {t('modals.delete.title')}
                    </h1>
                  </Dialog.Title>
                  <Dialog.Description className='text-sm text-cool-grey text-center'>
                    {t('modals.delete.description')}
                  </Dialog.Description>
                  <div className='w-full flex items-center justify-center gap-2'>
                    <Button variant='secondary' onClick={closeModal}>
                      {t('modals.delete.buttons.cancel')}
                    </Button>
                    <Button variant='primary' onClick={handleDelete}>
                      {t('modals.delete.buttons.delete')}
                      <Right size='16px' color='#FFFFFF' />
                    </Button>
                  </div>
                  {isLoading && <Loader />}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
};

export default DeleteModal;
